/**
 * Shared public-site policy.
 *
 * Contact channels, the legal-link map, and the policy versions live in
 * this module. The footer, `/contato`, the trust card, the plan-details
 * line, registration, and the legal shell are meant to read them from
 * here. This file does not rewire those screens.
 *
 * A channel whose `value` is empty is omitted by later readers: the icon,
 * the label, and any sentence that would name it. `LEGAL_LINK_MAP` stays
 * false until that page has lawyer text, so the footer, the trust card,
 * and the plan-details line omit `/termos`, `/privacidade`, and
 * `/reembolso`. The registration sentence may still link to the terms and
 * privacy routes. That sentence is not this map.
 *
 * `POLICY_VERSION` is the one constant registration sends and the legal
 * shell uses for its date line. `lastUpdated` is the date shown as
 * Última atualização.
 */

/** WhatsApp href already rendered by PublicSiteFooter. Not a new number. */
const WHATSAPP_HREF = "https://wa.me/5562999908635";

const INSTAGRAM_HREF = "https://www.instagram.com/acerteiquestoes/";

const INSTAGRAM_HANDLE = "@acerteiquestoes";

const SUPPORT_EMAIL = "suporte@acerteiquestoes.com.br";

export type ContactChannel = {
  id: "whatsapp" | "instagram" | "email";
  /** Visible name. */
  label: string;
  /**
   * Configured content. Empty means later readers omit the channel.
   * WhatsApp stores the href. Instagram stores the handle. Email stores
   * the address.
   */
  value: string;
  /** Link destination. Email uses mailto. Empty when `value` is empty. */
  href: string;
};

export const PUBLIC_CONTACT_CHANNELS: readonly ContactChannel[] = [
  {
    id: "whatsapp",
    label: "WhatsApp",
    value: WHATSAPP_HREF,
    href: WHATSAPP_HREF,
  },
  {
    id: "instagram",
    label: "Instagram",
    value: INSTAGRAM_HANDLE,
    href: INSTAGRAM_HREF,
  },
  {
    id: "email",
    label: "E-mail",
    value: SUPPORT_EMAIL,
    href: `mailto:${SUPPORT_EMAIL}`,
  },
];

/** True when later readers should render this channel. */
export function contactChannelHasValue(channel: ContactChannel): boolean {
  return channel.value.trim() !== "";
}

/** Channels a sentence, a footer, or `/contato` may name. */
export function contactChannelsWithValue(): readonly ContactChannel[] {
  return PUBLIC_CONTACT_CHANNELS.filter(contactChannelHasValue);
}

/**
 * Whether each legal route may be linked from the footer, the trust card,
 * and the plan-details line. False until that page has lawyer text.
 */
export const LEGAL_LINK_MAP = {
  termos: false,
  privacidade: false,
  reembolso: false,
} as const;

export type LegalLinkId = keyof typeof LEGAL_LINK_MAP;

export const LEGAL_LINK_ROUTES: Record<
  LegalLinkId,
  { readonly label: string; readonly to: string }
> = {
  termos: { label: "Termos de uso", to: "/termos" },
  privacidade: { label: "Política de privacidade", to: "/privacidade" },
  reembolso: { label: "Reembolso e cancelamento", to: "/reembolso" },
};

/** Routes whose map entry is true, in map order. Empty while every entry is false. */
export function availableLegalLinkIds(): readonly LegalLinkId[] {
  return (Object.keys(LEGAL_LINK_MAP) as LegalLinkId[]).filter(
    (id) => LEGAL_LINK_MAP[id],
  );
}

/**
 * Registration sends `termsVersion` and `privacyVersion`.
 * The legal shell shows `lastUpdated` after Última atualização.
 *
 * The date is 27/10/2026, the day the CEO froze this public copy.
 * It is the shell date. It is not a claim that lawyer text was approved.
 */
export const POLICY_VERSION = {
  termsVersion: "2026-10-27",
  privacyVersion: "2026-10-27",
  lastUpdated: "27/10/2026",
} as const;
